import { Trophy, Sparkles, ArrowUpRight, Mail } from "lucide-react";
import { MILESTONES, getLastAchieved, getNext } from "@/lib/milestones";

/**
 * Visual celebration of subscriber growth. Shows:
 *   - current count (large)
 *   - next milestone with progress bar
 *   - badge of last milestone already passed
 *   - optional "send weekly summary now" button
 *
 * Lives at the top of the admin newsletter dashboard, just after stats.
 */
const SubscriberMilestone = ({ count = 0, onSendWeeklySummary, loading = false }) => {
  const lastAchieved = getLastAchieved(count);
  const next = getNext(count);
  const prev = lastAchieved ? lastAchieved.count : 0;
  const total = MILESTONES[MILESTONES.length - 1].count;
  const progress = next ? Math.min(100, ((count - prev) / (next.count - prev)) * 100) : 100;
  const toGo = next ? next.count - count : 0;

  return (
    <div
      className="bg-gradient-to-br from-terracotta/8 via-ivory to-sage/10 rounded-3xl border border-terracotta/20 p-7 md:p-8 mt-6"
      data-testid="subscriber-milestone"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-terracotta" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-terracotta font-medium">
              Crecimiento orgánico
            </span>
          </div>
          <p className="font-serif text-5xl md:text-6xl text-charcoal" data-testid="milestone-count">
            {count}
            <span className="text-charcoal/40 text-2xl md:text-3xl font-light ml-2">suscriptoras</span>
          </p>
          {lastAchieved && (
            <p className="mt-3 text-[13px] text-charcoal/70" data-testid="milestone-last-achieved">
              <span className="text-lg mr-1">{lastAchieved.emoji}</span>
              Último hito conseguido: <strong className="text-charcoal">{lastAchieved.label}</strong>
            </p>
          )}
        </div>

        {next ? (
          <div className="text-right shrink-0">
            <p className="text-[10px] tracking-[0.28em] uppercase text-charcoal/55 mb-1">Próximo hito</p>
            <p className="font-serif text-2xl md:text-3xl text-terracotta flex items-center gap-2 justify-end" data-testid="milestone-next">
              <span>{next.emoji}</span>
              {next.count}
            </p>
            <p className="text-[12px] text-charcoal/60 mt-1">
              {next.label} · <strong>faltan {toGo}</strong>
            </p>
          </div>
        ) : (
          <div className="text-right shrink-0">
            <Trophy size={28} className="text-terracotta inline-block" />
            <p className="font-serif italic text-charcoal mt-1">Todos los hitos conseguidos.</p>
            <p className="text-[11px] text-charcoal/55 mt-1">{total} suscriptoras alcanzadas</p>
          </div>
        )}
      </div>

      {next && (
        <div className="mt-6">
          <div
            className="h-2 bg-sand/70 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full bg-gradient-to-r from-terracotta to-terracotta/70 transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="milestone-progress-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-charcoal/55">
            <span>{prev}</span>
            <span className="inline-flex items-center gap-1">
              {Math.round(progress)}% <ArrowUpRight size={11} />
            </span>
            <span>{next.count}</span>
          </div>
        </div>
      )}

      {onSendWeeklySummary && (
        <div className="mt-6 pt-5 border-t border-sand/60 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[12.5px] text-charcoal/65 leading-relaxed max-w-md">
            Recibes este resumen automáticamente cada <strong>lunes 09:00</strong>. ¿Quieres una copia ahora?
          </p>
          <button
            type="button"
            onClick={onSendWeeklySummary}
            disabled={loading}
            data-testid="send-weekly-summary-btn"
            className="inline-flex items-center gap-2 border border-charcoal/20 text-charcoal rounded-full px-5 py-2.5 text-[13px] hover:border-charcoal/50 transition-colors disabled:opacity-50"
          >
            <Mail size={14} /> Enviarme el resumen
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriberMilestone;
